// Issue / revoke / rebind for one tenant.
//
// KEY DISCLOSURE. The plaintext lives in this component's `useState` and
// NOWHERE else — never in the query cache. That is what makes "we cannot show
// this to you again" true rather than aspirational: a refetch, a remount or a
// navigation loses it, exactly as the backend has already lost it (only the
// sha256 is stored). `screens/ops/api.ts::useMintLicenseKey` documents the same
// rule for the tenant-facing path.
//
// Naming trap: `crossCutting.test.ts` greps for a plaintext key read off a
// license payload. Keep `issued.key` — naming the mutation result `license`
// turns that guard red for a correct reason.

import { useState } from "react";
import { Button, Field, Input, Panel, Toast } from "../../ui";
import { CopyField } from "../ops/CopyField";
import { ConfirmButton } from "./ConfirmButton";
import { useIssueKey, usePatchLicense } from "./api";
import type { LicenseKeyRow } from "./api";

export function LicenseActions({
  orgSlug,
  keys,
}: {
  orgSlug: string;
  keys: LicenseKeyRow[];
}) {
  const [minted, setMinted] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [domains, setDomains] = useState("");
  const issue = useIssueKey(orgSlug);
  const patch = usePatchLicense(orgSlug);

  const primary = keys.find((k) => k.status === "active") ?? keys[0] ?? null;

  return (
    <div className="maisight-admin-actions">
      {minted ? (
        <Panel tone="inset">
          <p className="type-bodyS" role="alert">
            Copy this now. It is the only time this key will be shown — only its
            sha256 is stored, so a lost key has to be reissued.
          </p>
          <CopyField label="License key" value={minted} />
          <Button variant="primary" size="sm" onClick={() => setMinted(null)}>
            I've saved it
          </Button>
        </Panel>
      ) : (
        <Button
          variant="primary"
          size="sm"
          onClick={() =>
            issue.mutate(
              { plan: primary?.plan ?? "pro", domains: primary?.domains ?? [] },
              { onSuccess: (issued) => setMinted(issued.key) },
            )
          }
        >
          {issue.isPending ? "Issuing…" : "Issue a new key"}
        </Button>
      )}

      {primary ? (
        <div className="maisight-admin-rebind">
          <Field label="Bound domains" help="Comma separated. `*.example.com` is allowed.">
            <Input
              value={domains}
              placeholder={primary.domains.join(", ")}
              onChange={(e) => setDomains(e.currentTarget.value)}
            />
          </Field>
          <ConfirmButton
            label="Rebind domains"
            confirmLabel="Confirm rebind"
            consequence={`Rebinding stops mAiSight rendering on ${primary.domains.join(", ") || "the current domains"} immediately.`}
            pending={patch.isPending}
            onConfirm={() =>
              patch.mutate(
                {
                  keyId: primary.key_id,
                  patch: {
                    domains: domains
                      .split(",")
                      .map((d) => d.trim())
                      .filter(Boolean),
                  },
                },
                { onSuccess: () => setNotice("Domains rebound.") },
              )
            }
          />
        </div>
      ) : null}

      {notice ? (
        <Toast title={notice} tone="nominal" onDismiss={() => setNotice(null)} />
      ) : null}
    </div>
  );
}
